import React from 'react';
import { Gift, Heart, Home, Users, Camera, Star, Clock, CheckCircle } from 'lucide-react';

const WeddingPage = ({ setCurrentPage }) => {
  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-rose-50 to-error-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">💍</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Bryllupsgaver</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              En bryllupsgave skal feire kjærligheten og hjelpe det lykkelige paret med å bygge sitt nye liv sammen. 
              Her får du veiledning til å velge den perfekte gaven.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-rose-600 text-white rounded-lg hover:from-primary-600 hover:to-rose-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn den perfekte bryllupsgaven
            </button>
          </div>
        </div>
      </section>

      {/* Planning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hva bør du tenke på når du velger bryllupsgave?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-primary-50 to-rose-50 rounded-lg p-6 border border-primary-100">
                <Heart className="w-12 h-12 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Relasjon til paret</h3>
                <p className="text-secondary-600">
                  Hvor nært kjente er du parets? Familie og nære venner gir ofte mer personlige og dyrere gaver, 
                  mens kolleger og bekjente kan velge noe mindre kostbart men like gjennomtenkt.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Users className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Parets livsstil</h3>
                <p className="text-secondary-600">
                  Tenk på hvordan paret lever. Er de praktiske eller liker de luksus? Lager de mat hjemme eller 
                  spiser ute? Reiser de mye eller er de hjemmekjære? Dette påvirker hva som vil være nyttig.
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Home className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Hjemmesituasjon</h3>
                <p className="text-secondary-600">
                  Flytter de sammen for første gang eller har de allerede etablert et hjem? 
                  Nye par trenger ofte grunnleggende ting, mens etablerte par kan sette pris på oppgraderinger.
                </p>
              </div>
            </div>

            {/* Timing Tips */}
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6 mb-12">
              <div className="flex items-start space-x-4">
                <Clock className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">Viktig timing-tips</h3>
                  <p className="text-secondary-700">
                    Du har faktisk opptil ett år etter bryllupet til å gi gave! Mange par setter pris på gaver 
                    som kommer etter bryllupet når hverdagen har satt seg. De fleste gaver sendes 2-8 uker før bryllupet.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Populære bryllupsgave-kategorier
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Traditional Gifts */}
              <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                <div className="flex items-center mb-6">
                  <Home className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Klassiske hjemgaver</h3>
                </div>
                <ul className="space-y-3 text-secondary-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Servise og glass:</strong> Elegant porselen, vinglass eller kaffe-sett for festlige anledninger</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Kjøkkenutstyr:</strong> Høykvalitets kniver, kjøkkenmaskiner eller kokkeutstyr</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Sengetøy og håndklær:</strong> Luksuriøse tekstiler i god kvalitet som varer i årevis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Dekorative gjenstander:</strong> Vaser, lysestaker eller kunstverk for hjemmet</span>
                  </li>
                </ul>
              </div>

              {/* Modern Gifts */}
              <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                <div className="flex items-center mb-6">
                  <Star className="w-8 h-8 text-purple-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Moderne alternativer</h3>
                </div>
                <ul className="space-y-3 text-secondary-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Opplevelser:</strong> Romantiske weekender, konsertbilletter eller kurs de kan ta sammen</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Smarte hjemløsninger:</strong> Smart belysning, sikkerhetssystemer eller hjemme-assistenter</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Abonnementer:</strong> Vinabonnement, streaming-tjenester eller måltidskasser</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Personaliserte gaver:</strong> Graverte gjenstander, tilpasset kunst eller fotografier</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Guidelines */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Budsjettveiledning for bryllupsgaver
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 text-center border border-success-100">
                <div className="text-3xl font-bold text-success-600 mb-2">500-1000 kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Bekjente & kolleger</h3>
                <p className="text-secondary-600 text-sm">
                  Pent gavekort, mindre hjemting eller bidrag til større fellesgave
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 text-center border border-accent-100">
                <div className="text-3xl font-bold text-accent-600 mb-2">1000-2500 kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Venner</h3>
                <p className="text-secondary-600 text-sm">
                  Kvalitetsprodukter for hjemmet eller fine opplevelser
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 text-center border border-purple-100">
                <div className="text-3xl font-bold text-purple-600 mb-2">2500+ kr</div>
                <h3 className="text-lg font-semibold text-secondary-800 mb-2">Familie & nære venner</h3>
                <p className="text-secondary-600 text-sm">
                  Luksusgaver, store opplevelser eller betydningsfulle arveting
                </p>
              </div>
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Gift className="w-6 h-6 text-accent-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">Husk ønskelisten!</h3>
                  <p className="text-secondary-700">
                    Mange par lager ønskelister hos butikker eller online. Dette er den sikreste måten å gi noe 
                    de faktisk trenger og ønsker seg. Spør gjerne brudeparet eller deres nærmeste familie om de har en liste.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Touch Section */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-rose-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Camera className="w-16 h-16 text-primary-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-secondary-800 mb-6">
              Det personlige preget
            </h2>
            <div className="text-left bg-white rounded-lg shadow-sm p-8 border border-primary-100">
              <p className="text-secondary-700 text-lg leading-relaxed mb-6">
                Det som gjør en bryllupsgave virkelig spesiell er det personlige preget. Det kan være så enkelt som 
                en gjennomtenkt kort med en historie om paret, eller så omfattende som en personalisert gave 
                som reflekterer deres unike kjærlighetshistorie.
              </p>
              
              <h3 className="text-xl font-semibold text-secondary-800 mb-4">Ideer for personalisering:</h3>
              <ul className="space-y-2 text-secondary-700">
                <li>• Graver inn parets navn, bryllupsdato eller et meningsfullt sitat</li>
                <li>• Lag en fotobok med minner fra forholdet deres</li>
                <li>• Velg noe som reflekterer deres felles interesser eller hobbyer</li>
                <li>• Gi en gave som har en historie - kanskje noe fra stedet de møttes</li>
                <li>• Skriv et hjertelig kort som forklarer tankene bak gavevalgetet</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-rose-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte bryllupsgaven?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Bruk vår gavefinner for å få personlige anbefalinger basert på parets interesser og ditt budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('occasion')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition-colors font-semibold"
              >
                Se andre anledninger
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeddingPage;